import {
	formatPhoneNumber,
	getCursorIndexForDigitCount,
	getDigitCountBeforeIndex,
	usesPaddedPhoneMask,
} from '../../../utils/formatPhoneNumber'

/**
 * Input
 */
export class GMXInput extends HTMLElement {
	inputEl: HTMLInputElement

	private boundInputEventHandler = this._handleInputEvent.bind(this)
	private boundFocusEventHandler = this._handleFocusEvent.bind(this)
	private boundBlurEventHandler = this._handleBlurEvent.bind(this)

	connectedCallback(): void {
		this.inputEl = this.querySelector('input')

		if (this.inputEl) {
			this.inputEl.addEventListener('input', this.boundInputEventHandler)
			this.inputEl.addEventListener('focus', this.boundFocusEventHandler)
			this.inputEl.addEventListener('blur', this.boundBlurEventHandler)
		} else {
			window.console.error(`ERROR:  GMX Input missing <input> element`)
		}
	}

	disconnectedCallback(): void {
		if (this.inputEl) {
			this.inputEl.removeEventListener('input', this.boundInputEventHandler)
			this.inputEl.removeEventListener('focus', this.boundFocusEventHandler)
		}
	}

	/**
	 * Phone formatting applies only to `type="tel"` fields that declare a country.
	 */
	private _isPhoneInput(): boolean {
		return this.inputEl.type === 'tel' && Boolean(this.inputEl.dataset.countryCode)
	}

	private _phoneCountryCode(): string {
		return this.inputEl.dataset.countryCode || ''
	}

	/**
	 * Restore the caret after a padded NANP reformat. Offsets match the
	 * `(XXX) XXX-XXXX` mask: area code sits after `(`, and the exchange sits
	 * after `) `.
	 */
	private _setPaddedPhoneCursor(numbers: string[], fromFocus = false): void {
		if (fromFocus) {
			if (numbers.length < 3) {
				this.inputEl.setSelectionRange(numbers.length + 1, numbers.length + 1)
			} else if (numbers.length >= 3 && numbers.length <= 6) {
				this.inputEl.setSelectionRange(numbers.length + 3, numbers.length + 3)
			}

			return
		}

		if (numbers.length <= 3) {
			this.inputEl.setSelectionRange(numbers.length + 1, numbers.length + 1)
		} else if (numbers.length > 3 && numbers.length <= 6) {
			this.inputEl.setSelectionRange(numbers.length + 3, numbers.length + 3)
		}
	}

	/**
	 * Method to handle when the component's inner <input> element's value changes
	 * @private
	 */
	private _handleInputEvent(): void {
		if (this.inputEl.classList.contains('cc-number')) {
			this.inputEl.value = this.formatCCNumber(this.inputEl.value)
		} else if (this._isPhoneInput()) {
			const countryCode = this._phoneCountryCode()
			const cursor = this.inputEl.selectionStart ?? this.inputEl.value.length
			const digitsBeforeCursor = getDigitCountBeforeIndex(this.inputEl.value, cursor)

			this.inputEl.value = this.formatPhoneNumber(this.inputEl.value, countryCode)

			if (usesPaddedPhoneMask(countryCode)) {
				this._setPaddedPhoneCursor(this.inputEl.value.replace(/\D/g, '').split(''))
			} else {
				const nextCursor = getCursorIndexForDigitCount(this.inputEl.value, digitsBeforeCursor)
				this.inputEl.setSelectionRange(nextCursor, nextCursor)
			}
		}

		if (this.inputEl.value) {
			this.inputEl.classList.add('filled')
		} else {
			this.inputEl.classList.remove('filled')
		}
	}

	/**
	 * Method to handle focus into the input field
	 * @private
	 */
	private _handleFocusEvent(): void {
		if (!this._isPhoneInput() || !usesPaddedPhoneMask(this._phoneCountryCode())) {
			return
		}

		if (!this.inputEl.value) {
			this.inputEl.value = this.formatPhoneNumber('', this._phoneCountryCode())

			setTimeout(() => {
				this.inputEl.setSelectionRange(1, 1)
			}, 0)
		} else {
			const numbers = this.inputEl.value.replace(/\D/g, '').split('')

			setTimeout(() => {
				this._setPaddedPhoneCursor(numbers, true)
			}, 0)
		}
	}

	/**
	 * Method to handle blur out of the input field
	 * @private
	 */
	private _handleBlurEvent(): void {
		if (!this._isPhoneInput() || !usesPaddedPhoneMask(this._phoneCountryCode())) {
			return
		}

		const numbers = this.inputEl.value.replace(/\D/g, '').split('')

		if (numbers.length === 0) {
			this.inputEl.value = ''
			this.inputEl.classList.remove('filled')
		}
	}

	/**
	 * Method to format at credit card number
	 * @param ccnumber
	 */
	formatCCNumber(ccnumber: string): string {
		if (ccnumber.startsWith('34') || ccnumber.startsWith('37')) {
			// Amex
			const sanitized = ccnumber.replace(/\D/g, '').slice(0, 15);
			return sanitized.replace(/(.{4})(.{0,6})(.{0,5})/g, '$1 $2 $3').trim();
		} else {
			// Non-amex
			const sanitized = ccnumber.replace(/\D/g, '').slice(0, 16);
			return sanitized.replace(/(.{4})/g, '$1 ').trim();
		}
	}

	/**
	 * Method to format a phone number for the given country code
	 */
	formatPhoneNumber(phonenum: string, countryCode?: string): string {
		return formatPhoneNumber(phonenum, countryCode || this._phoneCountryCode())
	}

	/**
	 * Method to format phone number (US / NANP)
	 */
	formatUSPhoneNumber(phonenum: string): string {
		return formatPhoneNumber(phonenum, 'us')
	}
}
if (!customElements.get('gmx-input')) {
	customElements.define('gmx-input', GMXInput)
}
